import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  ContractReleaseOrderRecord, CreateContractReleaseOrderPayload, EnterpriseContractRecord, SalesApiService
} from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-enterprise-contract-detail',
  imports: [
    AinAmountPipe, AinDatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, TableModule, DialogModule, CustomerSelectorComponent,
    CurrencySelectorComponent, JalaliDateInputComponent, SelectModule
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.contracts.form.createTitle') : t('sales.contracts.form.editTitle')" [description]="t('sales.contracts.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/sales/contracts']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
            } @else {
              <button pButton type="button" [label]="t('sales.contracts.addRelease')" [disabled]="saving()" (click)="showReleaseDialog.set(true)"></button>
            }
          </div>
        </header>
        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.contracts.fields.contractNumber') }}</span><input pInputText [(ngModel)]="form.contractNumber" /></label>
            <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" (ngModelChange)="onCustomerChange($event)" /></label>
            <label><span>{{ t('sales.contracts.fields.proforma') }}</span>
              <p-select [options]="proformaOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.proformaSalesOrderId" [showClear]="true" [disabled]="!form.customerAccountId" appendTo="body" />
            </label>
            <label><span>{{ t('sales.contracts.fields.totalValue') }}</span><p-inputNumber [(ngModel)]="form.totalContractValue" [min]="0" /></label>
            <label><span>{{ t('sales.contracts.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
            <label><span>{{ t('sales.contracts.fields.effectiveFrom') }}</span><ain-date-input [(ngModel)]="form.effectiveFromUtc" /></label>
          </section>
        } @else if (contract()) {
          <section class="card summary">
            <article><span>{{ t('sales.contracts.fields.contractNumber') }}</span><strong>{{ contract()!.contractNumber }}</strong></article>
            <article><span>{{ t('sales.contracts.fields.totalValue') }}</span><strong>{{ contract()!.totalContractValue | ainAmount }} {{ contract()!.currencyCode }}</strong></article>
            <article><span>{{ t('columns.status') }}</span><strong>{{ contract()!.status }}</strong></article>
            @if (contract()!.proformaSalesOrderId) {
              <article>
                <span>{{ t('sales.contracts.fields.proforma') }}</span>
                <a class="proforma-link" [routerLink]="['/sales/quotes', contract()!.proformaSalesOrderId]">{{ proformaLabel() }}</a>
              </article>
            }
          </section>
          <h3>{{ t('sales.contracts.tabs.releaseOrders') }}</h3>
          <p-table [value]="releases()" [paginator]="true" [rows]="12">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('sales.contracts.fields.releaseNumber') }}</th>
                <th>{{ t('sales.contracts.fields.releaseAmount') }}</th>
                <th>{{ t('columns.status') }}</th>
                <th>{{ t('sales.contracts.fields.releasedAt') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.releaseNumber }}</td>
                <td>{{ row.releaseAmount | ainAmount }} {{ contract()?.currencyCode }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.releasedAtUtc ? (row.releasedAtUtc | ainDate:'datetime') : '-' }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
    <p-dialog [header]="t('sales.contracts.addRelease')" [visible]="showReleaseDialog()" (visibleChange)="showReleaseDialog.set($event)" [modal]="true" [style]="{width:'360px'}">
      <label><span>{{ t('sales.contracts.fields.releaseNumber') }}</span><input pInputText [(ngModel)]="releaseForm.releaseNumber" /></label>
      <label><span>{{ t('sales.contracts.fields.releaseAmount') }}</span><p-inputNumber [(ngModel)]="releaseForm.releaseAmount" [min]="0" /></label>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="submitRelease()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`.card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px;background:#fff}.toolbar{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px}.back-link{color:#1d4ed8;font-weight:600;text-decoration:none}.grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}label{display:grid;gap:6px}.summary{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px;font-size:14px}.summary article{display:grid;gap:4px}.summary span{color:#6b7280;font-size:12px}.proforma-link{color:#1d4ed8;font-weight:600;text-decoration:none}.error{color:#b91c1c}h3{margin:0 0 8px;font-size:15px}`]
})
export class SalesEnterpriseContractDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly contract = signal<EnterpriseContractRecord | null>(null);
  readonly releases = signal<ContractReleaseOrderRecord[]>([]);
  readonly showReleaseDialog = signal(false);
  readonly proformaOptions = signal<{ label: string; value: string }[]>([]);
  readonly proformaLabel = signal('');

  form = {
    contractNumber: '',
    customerAccountId: '' as string | null,
    proformaSalesOrderId: null as string | null,
    totalContractValue: 0,
    currencyCode: 'IRR',
    effectiveFromUtc: null as string | null
  };
  releaseForm = { releaseNumber: '', releaseAmount: 0 };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('contractId');
    this.isCreateMode.set(!id || id === 'new');
    if (this.isCreateMode()) { this.loading.set(false); return; }
    this.loadContract(id!);
  }

  onCustomerChange(customerId: string | null): void {
    this.form.proformaSalesOrderId = null;
    if (!customerId) {
      this.proformaOptions.set([]);
      return;
    }
    this.api.listOrders({ documentKind: 'Proforma', customerAccountId: customerId, pageSize: 50 }).subscribe({
      next: page => this.proformaOptions.set(page.items.map(item => ({
        value: item.id,
        label: item.orderNumber || item.id
      }))),
      error: () => this.proformaOptions.set([])
    });
  }

  loadContract(id: string): void {
    this.loading.set(true);
    this.api.getEnterpriseContract(id).subscribe({
      next: c => {
        this.contract.set(c);
        if (c.proformaSalesOrderId) {
          this.api.getOrder(c.proformaSalesOrderId).subscribe({
            next: order => this.proformaLabel.set(order.orderNumber || c.proformaSalesOrderId!),
            error: () => this.proformaLabel.set(c.proformaSalesOrderId!)
          });
        } else {
          this.proformaLabel.set('');
        }
      },
      error: () => this.errorMessage.set(this.t('sales.contracts.errorLoad'))
    });
    this.api.listContractReleaseOrders(id).subscribe({
      next: rows => { this.releases.set(rows); this.loading.set(false); },
      error: () => { this.errorMessage.set(this.t('sales.contracts.errorLoad')); this.loading.set(false); }
    });
  }

  save(): void {
    if (!this.form.customerAccountId || !this.form.contractNumber.trim()) {
      this.errorMessage.set(this.t('sales.contracts.errorSave'));
      return;
    }
    this.saving.set(true);
    this.api.createEnterpriseContract({
      contractNumber: this.form.contractNumber,
      customerAccountId: this.form.customerAccountId,
      salesChannelId: null,
      proformaSalesOrderId: this.form.proformaSalesOrderId,
      currencyCode: this.form.currencyCode,
      totalContractValue: this.form.totalContractValue,
      effectiveFromUtc: this.form.effectiveFromUtc ?? new Date().toISOString(),
      effectiveUntilUtc: null,
      notes: null
    }).subscribe({
      next: c => void this.router.navigate(['/sales/contracts', c.id]),
      error: () => { this.errorMessage.set(this.t('sales.contracts.errorSave')); this.saving.set(false); }
    });
  }

  submitRelease(): void {
    const id = this.route.snapshot.paramMap.get('contractId')!;
    const payload: CreateContractReleaseOrderPayload = {
      releaseNumber: this.releaseForm.releaseNumber,
      releaseAmount: this.releaseForm.releaseAmount,
      salesOrderId: null,
      notes: null,
      releaseImmediately: true
    };
    this.saving.set(true);
    this.api.createContractReleaseOrder(id, payload).subscribe({
      next: () => { this.showReleaseDialog.set(false); this.saving.set(false); this.loadContract(id); },
      error: () => { this.errorMessage.set(this.t('sales.contracts.releaseError')); this.saving.set(false); }
    });
  }

  t(key: string): string { return this.localization.translate(key); }
}
